<template>
  <section :class="{ 'with-user-image': !!data.imageUri }">
    <LoadingIndicator v-if="submitting" />

    <img class="user-image" v-if="data.imageUri" :src="data.imageUri"
         :alt="$t('login.authorize-user-image-alt')" />

    <i18n path="login.authorize-greeting" tag="p">
      <template #name>
        <span class="param">{{ data.name }}</span>
      </template>
    </i18n>

    <i18n path="login.authorize-message" tag="p">
      <template #clientName>
        <span class="param">{{ data.clientName }}</span>
      </template>
      <template #ourName>
        <span class="param">{{ $t('ewn.display-name') }}</span>
      </template>
    </i18n>

    <img class="client-logo" v-if="data.logoUri" :src="data.logoUri"
         :alt="$t('login.authorize-client-logo-alt')" />

    <div class="invalid-feedback" v-if="errorMessage">{{ errorMessage }}</div>

    <div class="button-container">
      <router-link :to="{ path: '/login', query: { redirect_uri: currentUrl } }" class="button-tertiary">
        {{ $t('login.cancel-button') }}
      </router-link>
      <button type="button" class="primary" @click="authorize" :disabled="submitting">
        {{ $t('login.authorize-button') }}
      </button>
    </div>
  </section>
</template>

<script>
  import { defineComponent, onMounted, ref } from 'vue';
  import { stringify } from 'qs';
  import authApi from '@/authApi';
  import { useI18n } from '@/i18n';
  import LoadingIndicator from '@/modules/loading/components/LoadingIndicator';
  import { useRouter } from '@/router';
  import { href, redirect } from '@/router/location';

  export default defineComponent({
    name: 'Authorize',
    components: {
      LoadingIndicator
    },
    setup () {
      const submitting = ref(false);
      const errorMessage = ref(null);
      const data = ref({});
      const i18n = useI18n();
      const { currentRoute } = useRouter();
      onMounted(async () => {
        submitting.value = true;
        const res = await authApi.get('authorizeinfo', {
          params: {
            client_id: currentRoute.query.client_id
          }
        });
        if (!res) {
          // TODO: Should we go to the Error page or just retry periodically?
          // There isn't a great recourse since this occurs on page load and
          // we don't have connectivity. Nonetheless, this should be unlikely
          // to happen since the only entry point to this is from the server
          // and they were already able to load the site
          return;
        }
        data.value = res.data;
        submitting.value = false;
      });
      return {
        async authorize () {
          errorMessage.value = null;
          submitting.value = true;
          const data = {
            ...currentRoute.query,
            consented: true
          };
          const res = await authApi.post('connect/authorize', stringify(data));
          if (res && res.status === 200) {
            redirect(res.headers.location);
          } else {
            errorMessage.value = i18n.t(
              res ? 'login.unexpected-error' : 'login.network-error');
            submitting.value = false;
          }
        },
        currentUrl: href(),
        data,
        errorMessage,
        submitting
      };
    }
  });
</script>

<style lang="scss" scoped>
  @use '@/styles/modules/clearfix';
  @use '@/styles/modules/palette';

  section {
    position: relative;
    padding-bottom: 64px;

    &.with-user-image {
      padding-top: 64px;
      margin-top: 80px;
    }
  }

  p {
    margin-bottom: 16px;

    .param {
      font-weight: bold;
    }
  }

  .user-image {
    position: absolute;
    top: -48px;
    left: 50%;
    transform: translateX(-50%);
    background: #fefdf8;
    border: 3px solid #fefdf8;
    border-radius: 100%;
    box-shadow: 0 -12px 12px #042b35;
    width: 100px;
    height: 100px;
  }

  .client-logo {
    background-color: #fefdf8;
    border-radius: 2px;
    margin-top: 10px;
    height: 75px;
  }

  .button-container {
    @include clearfix.clearfix();

    position: absolute;
    right: 16px;
    bottom: 16px;

    button {
      margin: 0 8px;
    }
  }
</style>
